"use client";
/**
 * Zwaluw — Hans Kazàn exit-intent popup
 *
 * Toont een modaal venster met Hans Kazàn portret + inruilkorting-claim.
 * Trigger-logica:
 *   • Desktop: exit-intent (cursor verlaat viewport bovenaan) OR delayMs (25s)
 *   • Mobile: alleen delayMs (geen exit-intent gesture op touch)
 *   • Sessie-flag in sessionStorage zodat popup max 1x per browser-sessie verschijnt
 *
 * Suppress automatisch op conversie-pagina's waar al een form staat
 * (/gratis-brochure, /thuisadvies, /contact, /toonzaal) — geen dubbele CTA's.
 *
 * Bron: handoff #26 — "17 Exit popup".
 */
import * as React from "react";
import { getSeizoensActie } from "../../lib/zwaluw-actie";
import { pushDataLayer } from "../../lib/gtm";

// dataLayer event-namen — gebruikt voor popup-analytics (zie docs/popup-tracking.md).
// Constants zodat eventuele rename overal in 1 plek gebeurt.
const POPUP_NAME = "hans_kazan";
const POPUP_BRAND = "zwaluw";

export interface HansKazanPopupProps {
  /** Modus: "auto" = exit-intent + delay, "off" = nooit tonen, "always" = forceer (voor previews) */
  mode?: "auto" | "off" | "always";
  /** Delay in ms voor auto-trigger. Default 25 s. */
  delayMs?: number;
  /** SessionStorage key — verander om mensen opnieuw een popup te tonen na rebrand. */
  storageKey?: string;
  /** Href voor de primary CTA (default /gratis-brochure) */
  ctaHref?: string;
  /** Paden waar popup niet getoond moet worden (form-pagina's) */
  suppressOnPaths?: string[];
  /** Inruil-bedrag uit DB-setting (bv "1.500" / "2.100"). Default "2.100". */
  inruilAmount?: string;
}

const DEFAULT_SUPPRESS_PATHS = [
  "/gratis-brochure",
  "/thuisadvies",
  "/contact",
  "/toonzaal",
];

const CloseSvg = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

const ClockSvg = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const ArrowSvg = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const CheckSvg = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const TvSvg = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="5" width="20" height="13" rx="2" />
    <polyline points="8 21 12 17 16 21" />
  </svg>
);

export function HansKazanPopup({
  mode = "auto",
  delayMs = 40000,
  storageKey = "zw-popup-kazan-v1",
  ctaHref = "/gratis-brochure",
  suppressOnPaths = DEFAULT_SUPPRESS_PATHS,
  inruilAmount = "2.100",
}: HansKazanPopupProps) {
  const [open, setOpen] = React.useState(false);
  const shownRef = React.useRef(false);
  const [closing, setClosing] = React.useState(false);
  // Tijdstip waarop popup verscheen — voor time_open_ms berekening bij close.
  const openedAtRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (mode === "off") return;
    if (typeof window === "undefined") return;

    // Skip op conversie-pagina's
    const path = window.location.pathname.replace(/\/$/, "");
    if (suppressOnPaths.some((p) => path === p || path.startsWith(p + "/"))) return;

    // Skip als al getoond deze sessie
    try {
      if (sessionStorage.getItem(storageKey)) return;
    } catch {
      /* sessionStorage onbereikbaar — toon gewoon */
    }

    const show = (trigger: "delay" | "exit_intent" | "always") => {
      if (shownRef.current) return;
      shownRef.current = true;
      openedAtRef.current = Date.now();
      try {
        sessionStorage.setItem(storageKey, "1");
      } catch {
        /* ignore */
      }
      setOpen(true);
      // Track impression
      pushDataLayer({
        event: "popup_shown",
        popup: POPUP_NAME,
        brand: POPUP_BRAND,
        trigger,
      });
    };

    if (mode === "always") {
      show("always");
      return;
    }

    // Auto-mode: delay OR exit-intent
    const timer = window.setTimeout(() => show("delay"), delayMs);

    const onMouseLeave = (e: MouseEvent) => {
      // Cursor verlaat viewport bovenaan → user is van plan weg te gaan
      if (e.clientY <= 8 && !e.relatedTarget) show("exit_intent");
    };

    document.addEventListener("mouseout", onMouseLeave);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseLeave);
    };
  }, [mode, delayMs, storageKey, suppressOnPaths]);

  React.useEffect(() => {
    if (!open) return;
    // Block scroll van underlying page
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose("esc");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // method = wat triggert de close: 'x' (close-button), 'backdrop' (click buiten),
  // 'esc' (Escape-toets), 'cta' (klik op primaire CTA-link)
  const handleClose = (method: "x" | "backdrop" | "esc" | "cta" = "x") => {
    const timeOpenMs = openedAtRef.current ? Date.now() - openedAtRef.current : 0;
    pushDataLayer({
      event: method === "cta" ? "popup_cta_click" : "popup_close",
      popup: POPUP_NAME,
      brand: POPUP_BRAND,
      close_method: method,
      time_open_ms: timeOpenMs,
    });
    setClosing(true);
    window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 250);
  };

  if (!open) return null;

  // Seizoens-actie label rolt automatisch mee (lente → zomer → herfst → winter)
  const actie = getSeizoensActie();

  return (
    <div
      className={`zw-popup-backdrop${closing ? " is-closing" : ""}`}
      onClick={() => handleClose("backdrop")}
      role="presentation"
    >
      <div
        className="zw-popup-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="zw-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="zw-popup-close"
          aria-label="Sluiten"
          onClick={() => handleClose("x")}
        >
          {CloseSvg}
        </button>

        <div className="zw-popup-photo">
          <div className="zw-popup-photo-attr">
            <div className="name">Hans Kazàn</div>
            <div className="role">
              {TvSvg}
              Bekend van TV
            </div>
          </div>
        </div>

        <div className="zw-popup-body">
          <span className="zw-popup-chip">
            {ClockSvg}
            {actie.label} · {actie.endLong}
          </span>
          <h2 id="zw-popup-title">
            Loop deze
            <br />
            <em>inruilkorting niet mis.</em>
          </h2>
          <p>
            Vraag nu de gratis brochure aan en wij rekenen tot € {inruilAmount}
            inruilwaarde van uw oude bad of douche mee — direct van de prijs.
          </p>

          <div className="zw-popup-amount">
            <div className="euro">€&nbsp;{inruilAmount}</div>
            <div className="label">
              <strong>Inruilwaarde</strong>
              op uw oude bad of douche
            </div>
          </div>

          <a className="zw-popup-cta" href={ctaHref} onClick={() => handleClose("cta")}>
            Claim mijn inruilkorting
            {ArrowSvg}
          </a>

          <div className="zw-popup-fine">
            <span>{CheckSvg} Gratis</span>
            <span>{CheckSvg} 30 sec.</span>
            <span>{CheckSvg} Vrijblijvend</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HansKazanPopup;
